import { useState } from 'react';
import { MessageSquare, Edit2, Trash2, Check, X, Clock } from 'lucide-react';

/**
 * ConversationHistory Component
 * Displays list of saved conversations with rename and delete functionality
 */
export default function ConversationHistory({ 
  conversations = [], 
  currentConversationId = null,
  onSelectConversation,
  onRenameConversation,
  onDeleteConversation 
}) {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const handleStartEdit = (conversation) => {
    setEditingId(conversation?.id);
    setEditTitle(conversation?.title);
  };

  const handleSaveEdit = (conversationId) => {
    if (editTitle?.trim()) {
      onRenameConversation?.(conversationId, editTitle?.trim());
    }
    setEditingId(null);
    setEditTitle('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Agora';
      if (diffMins < 60) return `${diffMins}min atrás`;
      if (diffHours < 24) return `${diffHours}h atrás`;
      if (diffDays < 7) return `${diffDays}d atrás`;
      
      return date?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch (error) {
      return '';
    }
  };

  if (!conversations || conversations?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4 py-8 text-center">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
          <MessageSquare size={24} className="text-gray-400" />
        </div>
        <p className="text-sm text-gray-500">Nenhuma conversa salva</p>
        <p className="text-xs text-gray-400 mt-1">Suas conversas aparecerão aqui</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-1 p-2">
          {conversations?.map((conversation) => {
            const isActive = conversation?.id === currentConversationId;
            const isEditing = editingId === conversation?.id;

            return (
              <div
                key={conversation?.id}
                className={`group relative rounded-lg transition-colors ${
                  isActive ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-100'
                }`}
              >
                {isEditing ? (
                  <div className="flex items-center gap-2 p-3">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e?.key === 'Enter') handleSaveEdit(conversation?.id);
                        if (e?.key === 'Escape') handleCancelEdit();
                      }}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                    <button
                      onClick={() => handleSaveEdit(conversation?.id)}
                      className="p-1 text-green-600 hover:bg-green-50 rounded"
                      title="Salvar"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                      title="Cancelar"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => onSelectConversation?.(conversation?.id)}
                    className="w-full text-left p-3 flex items-start gap-3"
                  >
                    <div className="flex-none pt-0.5">
                      <MessageSquare 
                        size={16} 
                        className={isActive ? 'text-blue-600' : 'text-gray-400'} 
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        isActive ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {conversation?.title}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <Clock size={12} className="text-gray-400" />
                        <p className="text-xs text-gray-500">
                          {formatDate(conversation?.updatedAt || conversation?.createdAt)}
                        </p>
                      </div>
                    </div>

                    <div className="flex-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleStartEdit(conversation);
                        }}
                        className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                        title="Renomear"
                      >
                        <Edit2 size={14} className="text-gray-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          if (window.confirm('Deseja realmente excluir esta conversa?')) {
                            onDeleteConversation?.(conversation?.id);
                          }
                        }}
                        className="p-1.5 hover:bg-red-50 rounded transition-colors"
                        title="Excluir"
                      >
                        <Trash2 size={14} className="text-red-600" />
                      </button>
                    </div>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}